import React, { useState, useEffect, useMemo, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { useAuth, AuthUser } from './auth/AuthContext';
import LoginPage from './components/LoginPage';
import { ToastProvider, useToast } from './components/Toast';
import TagTemplatesPanel from './components/TagTemplatesPanel';
import ComplianceDashboard from './components/ComplianceDashboard';
import AuditLogView from './components/AuditLogView';
import TagStatistics from './components/TagStatistics';
import CostAnalyzer from './components/CostAnalyzer';
import NetworkSecurity from './components/NetworkSecurity';
import VmManager from './components/VmManager';
import ConnectionTroubleshooter from './components/ConnectionTroubleshooter';
import VmScheduler from './components/VmScheduler';
import VmDeployment from './components/VmDeployment';
import VmDashboard from './components/VmDashboard';
import ApprovalsPanel from './components/ApprovalsPanel';
import RunbooksPanel from './components/RunbooksPanel';
import OpsOverview from './components/OpsOverview';
import { extractErrorMessage } from './utils/errors';

// Same-origin default, matching every other component (and the CSP connect-src 'self').
// For local dev against a separately-hosted API set REACT_APP_API_URL.
const API_URL = process.env.REACT_APP_API_URL || '/api';

interface Subscription {
  id: string;
  name: string;
  state: string;
}

interface Resource {
  id: string;
  name: string;
  type: string;
  resourceGroup: string;
  subscriptionId: string;
  tags: Record<string, string>;
}

interface TagInput {
  key: string;
  value: string;
}

type SortField = 'name' | 'type' | 'resourceGroup';
type SortDirection = 'asc' | 'desc';


const App: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <ToastProvider>
      <AuthenticatedApp user={user!} onLogout={logout} />
    </ToastProvider>
  );
};

interface AuthenticatedAppProps {
  user: AuthUser;
  onLogout: () => void;
}

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = ({ user, onLogout }) => {
  const { showToast } = useToast();

  // Mirrors the API's authorization policies: tag writes need operator or admin.
  const canWrite = user.role === 'operator' || user.role === 'admin';

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<string>('');
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Tab state
  type Tab = 'overview' | 'resources' | 'compliance' | 'statistics' | 'audit' | 'approvals' | 'runbooks' | 'costs' | 'network' | 'vms' | 'dashboard' | 'connection' | 'scheduler' | 'deployment';
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [searchText, setSearchText] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [allResources, setAllResources] = useState<Resource[]>([]);
  const [operationProgress, setOperationProgress] = useState<{ current: number; total: number } | null>(null);

  // Filter states
  const [filterResourceType, setFilterResourceType] = useState<string>('');
  const [filterResourceGroup, setFilterResourceGroup] = useState<string>('');
  const [filterTagKey, setFilterTagKey] = useState<string>('');
  const [filterTagValue, setFilterTagValue] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Sorting states
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Column visibility states
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    type: true,
    resourceGroup: true,
    tags: true
  });

  // Tag display selection
  const [selectedTagsToDisplay, setSelectedTagsToDisplay] = useState<Set<string>>(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Tag management state
  const [tagInputs, setTagInputs] = useState<TagInput[]>([{ key: '', value: '' }]);
  const [tagMode, setTagMode] = useState<'Merge' | 'Replace'>('Merge');
  const [dryRun, setDryRun] = useState(true);
  const [dryRunRemove, setDryRunRemove] = useState(true);
  const [removeTagNames, setRemoveTagNames] = useState('');

  // Import state
  const [importFile, setImportFile] = useState<File | null>(null);

  // Get unique values for filter dropdowns
  const uniqueResourceTypes = useMemo(() => {
    const types = Array.from(new Set(resources.map(r => r.type)));
    return types.sort();
  }, [resources]);

  const uniqueResourceGroups = useMemo(() => {
    const groups = Array.from(new Set(resources.map(r => r.resourceGroup)));
    return groups.sort();
  }, [resources]);

  const uniqueTagKeys = useMemo(() => {
    const keys = new Set<string>();
    resources.forEach(r => {
      Object.keys(r.tags).forEach(key => keys.add(key));
    });
    return Array.from(keys).sort();
  }, [resources]);

  // Newly appearing tag keys default to visible, but the user's manual
  // deselections survive resource reloads.
  const knownTagKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    setSelectedTagsToDisplay(prev => {
      const next = new Set<string>();
      uniqueTagKeys.forEach(key => {
        if (!knownTagKeys.current.has(key) || prev.has(key)) next.add(key);
      });
      knownTagKeys.current = new Set(uniqueTagKeys);
      return next;
    });
  }, [uniqueTagKeys]);

  // Filtered and sorted resources
  const processedResources = useMemo(() => {
    // First, filter
    let filtered = resources.filter(resource => {
      // Search text filter
      if (searchText && !resource.name.toLowerCase().includes(searchText.toLowerCase()) &&
          !resource.type.toLowerCase().includes(searchText.toLowerCase()) &&
          !resource.resourceGroup.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
      if (filterResourceType && resource.type !== filterResourceType) {
        return false;
      }
      if (filterResourceGroup && resource.resourceGroup !== filterResourceGroup) {
        return false;
      }
      if (filterTagKey) {
        if (!resource.tags[filterTagKey]) {
          return false;
        }
        if (filterTagValue && resource.tags[filterTagKey] !== filterTagValue) {
          return false;
        }
      }
      return true;
    });

    // Then, sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [resources, searchText, filterResourceType, filterResourceGroup, filterTagKey, filterTagValue, sortField, sortDirection]);

  useEffect(() => {
    loadSubscriptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSubscription) {
      loadResources();
    }
  }, [selectedSubscription]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubscriptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/resources/subscriptions`);
      setSubscriptions(response.data);

      if (response.data.length > 0 && !selectedSubscription) {
        setSelectedSubscription(response.data[0].id);
      }
    } catch (err: any) {
      showToast('Failed to load subscriptions. Is the API running?', 'error');
      console.error(err);
    }
  };

  const loadResources = async () => {
    if (!selectedSubscription) return;

    setLoading(true);
    setSelectedResources(new Set());
    clearFilters();

    try {
      const response = await axios.get(`${API_URL}/resources/${selectedSubscription}`);
      setResources(response.data);
    } catch (err: any) {
      showToast('Failed to load resources.', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllResources = async () => {
    try {
      const response = await axios.get(`${API_URL}/resources/all-subscriptions`);
      setAllResources(response.data);
    } catch (err: any) {
      console.error('Failed to load all resources:', err);
    }
  };

  // Load all resources when switching to statistics or compliance tabs
  useEffect(() => {
    if ((activeTab === 'statistics' || activeTab === 'compliance') && allResources.length === 0) {
      loadAllResources();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setFilterResourceType('');
    setFilterResourceGroup('');
    setFilterTagKey('');
    setFilterTagValue('');
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleColumn = (column: keyof typeof visibleColumns) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const toggleTagDisplay = (tagKey: string) => {
    const newSelected = new Set(selectedTagsToDisplay);
    if (newSelected.has(tagKey)) {
      newSelected.delete(tagKey);
    } else {
      newSelected.add(tagKey);
    }
    setSelectedTagsToDisplay(newSelected);
  };

  const selectAllTags = () => {
    setSelectedTagsToDisplay(new Set(uniqueTagKeys));
  };

  const deselectAllTags = () => {
    setSelectedTagsToDisplay(new Set());
  };

  const handleExport = async () => {
    if (!selectedSubscription) {
      showToast('Please select a subscription', 'warning');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/resources/export`,
        {
          subscriptionId: selectedSubscription,
          resourceGroupName: null,
          includeTags: true
        },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `azure-resources-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Resources exported successfully!', 'success');
    } catch (error) {
      showToast('Export failed. Please try again.', 'error');
      console.error('Export failed:', error);
    }
  };

  const exportAuditTrail = async () => {
    try {
      const response = await axios.get(`${API_URL}/audit/export`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-trail-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Audit trail exported!', 'success');
    } catch (error) {
      showToast('Audit export failed.', 'error');
      console.error('Audit export failed:', error);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      showToast('Please select a file to import', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/resources/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const result = response.data;

      if (result.successCount > 0) {
        showToast(`Import successful! ${result.successCount} resources updated.`, 'success');
        await loadResources();
      }

      if (result.failureCount > 0) {
        showToast(`Import completed with errors: ${result.failureCount} resources failed.`, 'warning');
      }

      setImportFile(null);
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      showToast(`Import failed: ${extractErrorMessage(err)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleResourceSelection = (resourceId: string) => {
    const newSelection = new Set(selectedResources);
    if (newSelection.has(resourceId)) {
      newSelection.delete(resourceId);
    } else {
      newSelection.add(resourceId);
    }
    setSelectedResources(newSelection);
  };

  const selectAll = () => {
    if (selectedResources.size === processedResources.length) {
      setSelectedResources(new Set());
    } else {
      setSelectedResources(new Set(processedResources.map(r => r.id)));
    }
  };

  const addTagInput = () => {
    setTagInputs([...tagInputs, { key: '', value: '' }]);
  };

  const removeTagInput = (index: number) => {
    setTagInputs(tagInputs.filter((_, i) => i !== index));
  };

  const updateTagInput = (index: number, field: 'key' | 'value', value: string) => {
    const newInputs = [...tagInputs];
    newInputs[index][field] = value;
    setTagInputs(newInputs);
  };

  const applyTags = async () => {
    if (selectedResources.size === 0) {
      showToast('Please select resources to tag', 'warning');
      return;
    }

    const validTags = tagInputs.filter(t => t.key && t.value);
    if (validTags.length === 0) {
      showToast('Please enter at least one tag', 'warning');
      return;
    }

    const tags: Record<string, string> = {};
    validTags.forEach(t => {
      tags[t.key] = t.value;
    });

    const requestPayload = {
      resourceIds: Array.from(selectedResources),
      tags,
      mode: tagMode,
      dryRun
    };

    try {
      setLoading(true);
      setOperationProgress({ current: 0, total: selectedResources.size });
      const response = await axios.post(`${API_URL}/resources/tags/apply`, requestPayload);
      if (response.data.approvalRequired) {
        showToast('Tag change submitted for approval — track it in the Approvals tab.', 'info');
        return;
      }

      const result = response.data;
      setOperationProgress({ current: result.successCount + result.failureCount, total: result.totalResources });

      showToast(
        `${dryRun ? '[DRY RUN] ' : ''}Successfully tagged ${result.successCount}/${result.totalResources} resources.` +
        (result.failureCount > 0 ? ` ${result.failureCount} failed.` : ''),
        dryRun ? 'info' : 'success'
      );

      if (!dryRun) {
        await loadResources();
        setTagInputs([{ key: '', value: '' }]);
      }
    } catch (err: any) {
      console.error('Tag application failed:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
      }
      showToast(`Failed to apply tags: ${extractErrorMessage(err)}`, 'error');
    } finally {
      setLoading(false);
      setTimeout(() => setOperationProgress(null), 2000);
    }
  };

  const removeTags = async () => {
    if (selectedResources.size === 0) {
      showToast('Please select resources', 'warning');
      return;
    }

    const tagNames = removeTagNames.split(',').map(t => t.trim()).filter(t => t);
    if (tagNames.length === 0) {
      showToast('Please enter tag names to remove', 'warning');
      return;
    }

    try {
      setLoading(true);
      setOperationProgress({ current: 0, total: selectedResources.size });

      const response = await axios.post(`${API_URL}/resources/tags/remove`, {
        resourceIds: Array.from(selectedResources),
        tagNames,
        dryRun: dryRunRemove
      });
      if (response.data.approvalRequired) {
        showToast('Tag removal submitted for approval — track it in the Approvals tab.', 'info');
        return;
      }

      const result = response.data;
      setOperationProgress({ current: result.successCount + result.failureCount, total: result.totalResources });

      if (dryRunRemove) {
        showToast(`Dry run completed. Tags would be removed from ${result.successCount} resources.`, 'info');
      } else {
        showToast(`Successfully removed tags from ${result.successCount}/${result.totalResources} resources.`, 'success');
        await loadResources();
        setRemoveTagNames('');
      }
    } catch (err: any) {
      showToast(`Failed to remove tags: ${extractErrorMessage(err)}`, 'error');
    } finally {
      setLoading(false);
      setTimeout(() => setOperationProgress(null), 2000);
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="App-header-row">
          <div style={{ width: '200px' }} />
          <div className="App-header-title">
            <h1>Azure Tag Manager</h1>
            <p>Manage Azure resource tags in bulk</p>
          </div>
          <div className="header-user">
            <span className={`mode-badge ${user.mode}`}>
              {user.mode === 'mock' ? 'Demo' : 'Azure'}
            </span>
            <span className={`mode-badge role-${user.role}`}>{user.role}</span>
            <span style={{ color: '#b0b0b0', fontSize: '0.9rem' }}>{user.username}</span>
            <button className="btn-logout" onClick={onLogout}>Sign out</button>
          </div>
        </div>
      </header>

      <nav className="tab-nav">
        {([
          { id: 'overview', label: '📟 Ops Overview' },
          { id: 'resources', label: '📋 Resources' },
          { id: 'compliance', label: '✅ Compliance' },
          { id: 'statistics', label: '📊 Statistics' },
          { id: 'audit', label: '📜 Audit Log' },
          { id: 'approvals', label: '🔏 Approvals' },
          { id: 'runbooks', label: '📘 Runbooks' },
          { id: 'costs', label: '💰 Cost Analyzer' },
          { id: 'network', label: '🛡️ Network Security' },
          { id: 'vms', label: '🖥️ VM Manager' },
          { id: 'dashboard', label: '📡 VM Dashboard' },
          { id: 'connection', label: '🔌 Connection Troubleshooter' },
          { id: 'scheduler', label: '⏰ Scheduler' },
          { id: 'deployment', label: '🚀 VM Deployment' },
        ] as const).map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="tab-content">
        {operationProgress && (
          <div className="progress-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b0b0b0', fontSize: '0.875rem' }}>
              <span>Operation in progress...</span>
              <span>{operationProgress.current} / {operationProgress.total}</span>
            </div>
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: `${operationProgress.total === 0 ? 0 : Math.round((operationProgress.current / operationProgress.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <>
            <div className="toolbar">
              <div className="subscription-selector">
                <label>Subscription:</label>
                <select
                  value={selectedSubscription}
                  onChange={(e) => setSelectedSubscription(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select subscription...</option>
                  {subscriptions.map(sub => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              <input
                type="text"
                className="search-bar"
                placeholder="Search resources..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />

              <button onClick={loadResources} disabled={loading || !selectedSubscription}>
                {loading ? 'Loading...' : '🔄 Refresh'}
              </button>
              <button onClick={handleExport} disabled={loading || !selectedSubscription}>
                📥 Export to Excel
              </button>
              <button onClick={exportAuditTrail} disabled={loading}>
                📄 Audit CSV
              </button>
              <button onClick={() => setShowFilters(!showFilters)} className="btn-filter">
                🔍 {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button onClick={() => setShowColumnSettings(!showColumnSettings)} className="btn-settings">
                ⚙️ {showColumnSettings ? 'Hide Settings' : 'Display Settings'}
              </button>
              <button onClick={() => setShowTemplates(!showTemplates)}>
                🗂️ {showTemplates ? 'Hide Templates' : 'Templates'}
              </button>
              <button onClick={selectAll}>
                {selectedResources.size === processedResources.length ? '☐ Deselect All' : '☑ Select All'}
              </button>
              <span className="selection-info">
                {selectedResources.size} of {processedResources.length} selected
                {processedResources.length !== resources.length &&
                  ` (${resources.length} total)`
                }
              </span>
            </div>

            {showColumnSettings && (
              <div className="settings-panel">
                <h3>Display Settings</h3>
                <div className="settings-group">
                  <h4>Visible Columns:</h4>
                  <div className="column-toggles">
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleColumns.name}
                        onChange={() => toggleColumn('name')}
                      />
                      Name
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleColumns.type}
                        onChange={() => toggleColumn('type')}
                      />
                      Type
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleColumns.resourceGroup}
                        onChange={() => toggleColumn('resourceGroup')}
                      />
                      Resource Group
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleColumns.tags}
                        onChange={() => toggleColumn('tags')}
                      />
                      Tags
                    </label>
                  </div>
                </div>

                {visibleColumns.tags && uniqueTagKeys.length > 0 && (
                  <div className="settings-group">
                    <h4>Displayed Tags:</h4>
                    <div className="tag-selection-controls">
                      <button onClick={selectAllTags} className="btn-small">Select All</button>
                      <button onClick={deselectAllTags} className="btn-small">Deselect All</button>
                    </div>
                    <div className="tag-checkboxes">
                      {uniqueTagKeys.map(tagKey => (
                        <label key={tagKey}>
                          <input
                            type="checkbox"
                            checked={selectedTagsToDisplay.has(tagKey)}
                            onChange={() => toggleTagDisplay(tagKey)}
                          />
                          {tagKey}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {showFilters && (
              <div className="filters-panel">
                <h3>Filters</h3>
                <div className="filter-controls">
                  <div className="filter-group">
                    <label>Resource Type:</label>
                    <select
                      value={filterResourceType}
                      onChange={(e) => setFilterResourceType(e.target.value)}
                    >
                      <option value="">All Types</option>
                      {uniqueResourceTypes.map(type => (
                        <option key={type} value={type}>
                          {type.split('/').pop()} ({type})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Resource Group:</label>
                    <select
                      value={filterResourceGroup}
                      onChange={(e) => setFilterResourceGroup(e.target.value)}
                    >
                      <option value="">All Groups</option>
                      {uniqueResourceGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Tag Key:</label>
                    <select
                      value={filterTagKey}
                      onChange={(e) => {
                        setFilterTagKey(e.target.value);
                        setFilterTagValue('');
                      }}
                    >
                      <option value="">All Tags</option>
                      {uniqueTagKeys.map(key => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  </div>

                  {filterTagKey && (
                    <div className="filter-group">
                      <label>Tag Value:</label>
                      <input
                        type="text"
                        placeholder="Enter tag value..."
                        value={filterTagValue}
                        onChange={(e) => setFilterTagValue(e.target.value)}
                      />
                    </div>
                  )}

                  <button onClick={clearFilters} className="btn-clear-filters">
                    Clear Filters
                  </button>
                </div>

                {processedResources.length < resources.length && (
                  <div className="filter-status">
                    Showing {processedResources.length} of {resources.length} resources
                  </div>
                )}
              </div>
            )}

            <div className="main-content">
              <div className="resources-section">
                <h2>Resources {selectedSubscription && `(${subscriptions.find(s => s.id === selectedSubscription)?.name})`}</h2>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={processedResources.length > 0 && selectedResources.size === processedResources.length}
                            onChange={selectAll}
                          />
                        </th>
                        {visibleColumns.name && (
                          <th onClick={() => handleSort('name')} className="sortable">
                            Name{getSortIndicator('name')}
                          </th>
                        )}
                        {visibleColumns.type && (
                          <th onClick={() => handleSort('type')} className="sortable">
                            Type{getSortIndicator('type')}
                          </th>
                        )}
                        {visibleColumns.resourceGroup && (
                          <th onClick={() => handleSort('resourceGroup')} className="sortable">
                            Resource Group{getSortIndicator('resourceGroup')}
                          </th>
                        )}
                        {visibleColumns.tags && <th>Tags</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {processedResources.length === 0 ? (
                        <tr>
                          <td colSpan={1 + Object.values(visibleColumns).filter(Boolean).length} style={{ textAlign: 'center', padding: '2rem' }}>
                            {loading ? 'Loading resources...' :
                             resources.length === 0 ? 'No resources found in this subscription' :
                             'No resources match the current filters'}
                          </td>
                        </tr>
                      ) : (
                        processedResources.map(resource => (
                          <tr key={resource.id} className={selectedResources.has(resource.id) ? 'selected' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedResources.has(resource.id)}
                                onChange={() => toggleResourceSelection(resource.id)}
                              />
                            </td>
                            {visibleColumns.name && <td>{resource.name}</td>}
                            {visibleColumns.type && <td>{resource.type.split('/').pop()}</td>}
                            {visibleColumns.resourceGroup && <td>{resource.resourceGroup}</td>}
                            {visibleColumns.tags && (
                              <td>
                                <div className="tags-cell">
                                  {Object.entries(resource.tags)
                                    .filter(([k]) => selectedTagsToDisplay.has(k))
                                    .map(([k, v]) => (
                                      <span key={k} className="tag">{k}: {v}</span>
                                    ))}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="actions-section">
                {!canWrite && (
                  <div className="action-card">
                    <h3>Read-only access</h3>
                    <p style={{ margin: 0, color: '#b0b0b0' }}>
                      Your role (<strong>{user.role}</strong>) can browse resources and tags.
                      Ask an administrator for the operator role to modify tags.
                    </p>
                  </div>
                )}

                {canWrite && showTemplates && (
                  <TagTemplatesPanel
                    currentTags={tagInputs}
                    onLoadTemplate={(tags) => setTagInputs(tags)}
                  />
                )}

                {canWrite && (<>
                <div className="action-card">
                  <h3>Apply Tags</h3>
                  <div className="tag-inputs">
                    {tagInputs.map((tag, index) => (
                      <div key={index} className="tag-input-row">
                        <input
                          type="text"
                          placeholder="Tag name"
                          value={tag.key}
                          onChange={(e) => updateTagInput(index, 'key', e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Tag value"
                          value={tag.value}
                          onChange={(e) => updateTagInput(index, 'value', e.target.value)}
                        />
                        <button
                          className="btn-remove"
                          onClick={() => removeTagInput(index)}
                          disabled={tagInputs.length === 1}
                        >
                          &#x2715;
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn-secondary" onClick={addTagInput}>+ Add Tag</button>

                  <div className="options">
                    <label>
                      <input
                        type="radio"
                        value="Merge"
                        checked={tagMode === 'Merge'}
                        onChange={(e) => setTagMode(e.target.value as 'Merge')}
                      />
                      Merge with existing tags
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="Replace"
                        checked={tagMode === 'Replace'}
                        onChange={(e) => setTagMode(e.target.value as 'Replace')}
                      />
                      Replace all tags
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={dryRun}
                        onChange={(e) => setDryRun(e.target.checked)}
                      />
                      Dry Run (preview only)
                    </label>
                  </div>

                  <button
                    className="btn-primary"
                    onClick={applyTags}
                    disabled={loading || selectedResources.size === 0}
                  >
                    {dryRun ? 'Preview Tag Changes' : 'Apply Tags'}
                  </button>
                </div>

                <div className="action-card">
                  <h3>Remove Tags</h3>
                  <input
                    type="text"
                    placeholder="Tag names (comma-separated)"
                    value={removeTagNames}
                    onChange={(e) => setRemoveTagNames(e.target.value)}
                  />
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={dryRunRemove}
                      onChange={(e) => setDryRunRemove(e.target.checked)}
                    />
                    Dry Run (preview only)
                  </label>
                  <button
                    className="btn-danger"
                    onClick={removeTags}
                    disabled={loading || selectedResources.size === 0}
                  >
                    {dryRunRemove ? 'Preview Tag Removal' : 'Remove Tags'}
                  </button>
                </div>

                <div className="action-card">
                  <h3>Import from Excel</h3>
                  <input
                    id="file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  />
                  <button
                    className="btn-primary"
                    onClick={handleImport}
                    disabled={loading || !importFile}
                  >
                    Import Resources
                  </button>
                </div>
                </>)}
              </div>
            </div>
          </>
        )}

        {activeTab === 'compliance' && (
          <ComplianceDashboard
            resources={allResources.length > 0 ? allResources : resources}
            onFixResources={(ids, tags) => {
              setSelectedResources(new Set(ids));
              setTagInputs(Object.entries(tags).map(([key, value]) => ({ key, value })));
              setActiveTab('resources');
            }}
          />
        )}

        {activeTab === 'statistics' && (
          <TagStatistics resources={resources} allResources={allResources} />
        )}

        {activeTab === 'overview' && <OpsOverview onNavigate={tab => setActiveTab(tab as Tab)} />}
        {activeTab === 'audit' && <AuditLogView />}
        {activeTab === 'approvals' && <ApprovalsPanel />}
        {activeTab === 'runbooks' && <RunbooksPanel />}
        {activeTab === 'costs' && <CostAnalyzer />}
        {activeTab === 'network' && <NetworkSecurity />}
        {activeTab === 'vms' && <VmManager />}
        {activeTab === 'dashboard' && <VmDashboard />}
        {activeTab === 'connection' && <ConnectionTroubleshooter />}
        {activeTab === 'scheduler' && <VmScheduler />}
        {activeTab === 'deployment' && <VmDeployment />}
      </main>
    </div>
  );
};

export default App;
